/**
 * Invisible timer component that records elapsed SIM time to Redux on unmount.
 *
 * @module components/sim/Timer
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Moment from 'moment';
import {saveElapsedTime} from '../../redux/sims/actions';

/**
 * Timer functional component tracks the time elapsed between its mount and unmount.
 *
 * This component utilizes a React effect to record the start time upon mounting
 * and the end time upon unmounting. It calculates the elapsed time in seconds
 * and invokes the provided `saveElapsedTime` callback with the duration value.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.saveElapsedTime - Callback function executed with the elapsed time
 * in seconds as its argument. This function is triggered when the component unmounts.
 *
 * @returns {React.Element} A React functional component that renders an empty
 * container tag.
 */
const Timer = ({saveElapsedTime}) => {
    useEffect(() => {
        const startTime = new Moment();
        return () => {
            const endTime = new Moment();
            const duration = Moment.duration(endTime.diff(startTime));
            saveElapsedTime(duration.asSeconds());
            //saveElapsedTime(duration.get('hour') > 0 ? `${duration.get('hour')}h
            // ${duration.get('minutes')}m` : `${duration.get('minutes')}m
            // ${duration.get('seconds')}s`);
        };
    }, [saveElapsedTime]);
    return (
        <div>
            {}
        </div>
    );
};

/**
 * PropTypes definition for the Timer component.
 * This defines the expected types and requirements
 * for the props passed to the Timer component.
 *
 * Properties:
 * - duration (number): The total duration of the timer in milliseconds.
 *   This is required.
 * - onComplete (func): Callback function to be invoked when the timer
 *   completes. This is optional.
 * - interval (number): The interval at which the timer updates, in
 *   milliseconds. Defaults to a predefined value if not provided.
 *   This is optional.
 */
Timer.propTypes = {
    saveElapsedTime: PropTypes.func,
};

/**
 * An object that maps dispatchable action creators to props for a connected React component.
 *
 * @property {Function} saveElapsedTime - Action creator function to dispatch an action for saving
 * elapsed time, typically used to store or update time-related data in the application's state.
 */
const mapDispatchToProps = {
    saveElapsedTime,
};

export default connect(null, mapDispatchToProps)(Timer);
